import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { FamilySummary } from "@/services/screen-budget/contracts";
import type { FamilyParentSummary } from "../useScreenBudgetAdminData";

interface ParentsCardProps {
  selectedFamilyId: number | null;
  selectedFamily: FamilySummary | null;
  currentUserId: number;
  parents: FamilyParentSummary[];
  newParentUsername: string;
  onNewParentUsernameChange: (value: string) => void;
  onAddParent: () => Promise<void>;
  isAddingParent: boolean;
  onRemoveParent: (userId: number, username: string) => Promise<void>;
  isRemovingParent: boolean;
}

export function ParentsCard({
  selectedFamilyId,
  selectedFamily,
  currentUserId,
  parents,
  newParentUsername,
  onNewParentUsernameChange,
  onAddParent,
  isAddingParent,
  onRemoveParent,
  isRemovingParent,
}: ParentsCardProps) {
  const handleAddParent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onAddParent();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Family Parents</CardTitle>
        <CardDescription>Add or remove parent members in the selected family.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form onSubmit={handleAddParent} className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="grid gap-2">
            <Label htmlFor="screen-budget-admin-parent">Username</Label>
            <Input
              id="screen-budget-admin-parent"
              value={newParentUsername}
              onChange={event => onNewParentUsernameChange(event.currentTarget.value)}
              placeholder="parent-user"
            />
          </div>
          <Button type="submit" className="self-end" disabled={selectedFamilyId === null || isAddingParent}>
            {isAddingParent ? "Adding..." : "Add parent"}
          </Button>
        </form>

        {parents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No parent members found for this family.</p>
        ) : (
          <div className="grid gap-2">
            {parents.map(parent => (
              <div key={`${parent.userId}-${parent.role}`} className="flex items-center justify-between rounded-md border px-3 py-2">
                <p className="text-sm">
                  {parent.username} <span className="text-muted-foreground">({parent.role})</span>
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground">Added {new Date(parent.createdAt).toLocaleString()}</p>
                  {selectedFamily?.role === "owner" && parent.role === "parent" && parent.userId !== currentUserId ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isRemovingParent}
                      onClick={() => void onRemoveParent(parent.userId, parent.username)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
